import React from "react";

interface Step {
  id: string;
  name: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  setCurrentStep: (step: number) => void;
}

export function Stepper({ steps, currentStep, setCurrentStep }: StepperProps) {
  const getStepStyles = (index: number) => {
    const isCompleted = index < currentStep;
    const isCurrent = index === currentStep;

    // Base styles that are common across all states
    const baseClasses =
      "group flex flex-col border-l-4 py-2 pl-2 md:border-l-0 md:pb-0 md:pl-0 cursor-pointer";
    const baseIdClasses =
      "top-[1px] relative text-xs italic font-medium font-mono uppercase tracking-widest";
    const baseLineClasses = "h-1.5 rounded-full flex-grow";
    const baseNameClasses = "";

    if (isCompleted) {
      return {
        container: `${baseClasses} border-indigo-400 hover:border-indigo-600`,
        line: `${baseLineClasses} bg-indigo-400`,
        id: `${baseIdClasses} text-indigo-400 group-hover:text-indigo-400 dark:text-slate-400 dark:group-hover:text-indigo-300`,
        name: `${baseNameClasses} text-slate-700 dark:text-slate-300`,
      };
    }

    if (isCurrent) {
      return {
        container: `${baseClasses} border-white`,
        line: `${baseLineClasses} bg-white`,
        id: `${baseIdClasses} text-white`,
        name: `${baseNameClasses} font-medium text-slate-900 dark:text-white`,
      };
    }

    return {
      container: `${baseClasses} border-slate-600 hover:border-gray-300`,
      line: `${baseLineClasses} bg-slate-600`,
      id: `${baseIdClasses} text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200`,
      name: `${baseNameClasses} text-gray-500 group-hover:text-gray-700 dark:text-gray-400 dark:group-hover:text-gray-200`,
    };
  };

  return (
    <nav aria-label="Progress">
      <ol role="list" className="space-y-4 md:flex md:space-x-4 md:space-y-0">
        {steps.map((step: Step, index: number) => {
          const styles = getStepStyles(index);
          const isCurrent = index === currentStep;

          return (
            <li key={step.name} className="md:flex-1">
              <a
                onClick={() => setCurrentStep(index)}
                className={styles.container}
                {...(isCurrent && { "aria-current": "step" })}
              >
                <div className="flex items-center gap-x-4">
                  <span className={styles.id}>{step.id}</span>
                  <div className={styles.line}></div>
                </div>
                <span className={styles.name}>{step.name}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
